import React from "react";
import { useLocation, Link } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Skeleton } from "@/components/ui/skeleton";
import { useAutomationServersQuery } from "@/hooks/useAutomationServersQuery";
import { useWorkspaceByIdQuery } from "@/hooks/useWorkspacesQuery";
import { useAutomations } from "@/context/AutomationsProvider";

const NamePlaceholder = () => (
  <Skeleton className="inline-block h-4 w-24 align-middle" />
);

const routeNameMap: Record<string, string> = {
  dashboard: "Dashboard",
  "automation-servers": "Automation Servers",
  workspaces: "Workspaces",
  processes: "Processes",
  automations: "Automations",
  settings: "Settings",
};

// Check if a string looks like an ID
function isUUID(str: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
}

// Find out if the ID is workspace, automation server, or process
function getEntityType(pathnames: string[], index: number): "automation-server" | "workspace" | "process" | null {
  const segment = pathnames[index];
  if (!segment || !isUUID(segment)) return null;

  // Check previous part of the path to determine entity type
  if (index > 0) {
    const prevSegment = pathnames[index - 1];
    if (prevSegment === "automation-servers") {
      return "automation-server";
    }
    if (prevSegment === "workspaces") {
      return "workspace";
    }
    if (prevSegment === "processes") {
      return "process";
    }
  }
  return null;
}

type AutomationServerCrumb = {
  href: string;
  isLast: boolean;
  entityType: "automation-server";
  serverId: string;
};

type WorkspaceCrumb = {
  href: string;
  isLast: boolean;
  entityType: "workspace";
  workspaceId: string;
};

type ProcessCrumb = {
  href: string;
  isLast: boolean;
  entityType: "process";
  processId: string;
};

type StaticCrumb = {
  href: string;
  isLast: boolean;
  entityType: null;
  name: string;
};

type BreadcrumbCrumb =
  | AutomationServerCrumb
  | WorkspaceCrumb
  | ProcessCrumb
  | StaticCrumb;

export function PageBreadcrumbs() {
  const location = useLocation();
  const pathnames = location.pathname.split("/").filter((x) => x);
  const {
    data: automationServersData,
    isLoading: automationServersLoading,
  } = useAutomationServersQuery();
  const { processes, isLoading: automationsLoading } = useAutomations();

    // Read previous path from sessionStorage, we can't use just path name because when we came to
    // /workspaces/:id from automations server the url includes only workspace path but in the breadcrumbs
    // we should see: Automation Servers > my-automation-server6 > Workspaces > dev-workspace2

  const previousPath = React.useMemo(() => {
    if (typeof window === "undefined") return null;
    return sessionStorage.getItem("previousPath");
  }, []);

  // Track previous route in sessionStorage
  React.useEffect(() => {
    const currentPath = location.pathname;
    
    // Store current path as previous for next navigation (only if it changed)
    if (previousPath !== currentPath) {
      sessionStorage.setItem("previousPath", currentPath);
    }
  }, [location.pathname, previousPath]);

  // Check if we're on a /workspaces/:id (not /automation-servers/:id/workspaces/:workspaceId)
  const isDirectWorkspaceRoute = 
    pathnames.length === 2 && 
    pathnames[0] === "workspaces" && 
    isUUID(pathnames[1]);

  // Check if user came from an automation server page
  const cameFromAutomationServer = React.useMemo(() => {
    if (typeof window === "undefined") return false;

    if (previousPath && /\/automation-servers\/[0-9a-f-]+/i.test(previousPath)) {
      return true;
    }
    
    const referrer = document.referrer;
    return /\/automation-servers\/[0-9a-f-]+/i.test(referrer);
  }, [previousPath]);

  if (pathnames.length === 0) {
    return null;
  }

  const breadcrumbs: BreadcrumbCrumb[] = pathnames.map((segment, index) => {
    let href = `/${pathnames.slice(0, index + 1).join("/")}`;
    // Special case: there is no /workspaces/:id/processes (list) route.
    // When we're on /workspaces/:id/processes/:processId, link "Processes" to the top-level /processes page.
    if (
      segment === "processes" &&
      index >= 2 &&
      pathnames[index - 2] === "workspaces" &&
      isUUID(pathnames[index - 1] ?? "")
    ) {
      href = "/processes";
    }
    const isLast = index === pathnames.length - 1;
    const entityType = getEntityType(pathnames, index);
    
    if (entityType === "automation-server") {
      return {
        href,
        isLast,
        entityType: "automation-server" as const,
        serverId: segment,
      };
    }
    if (entityType === "workspace") {
      return {
        href,
        isLast,
        entityType: "workspace" as const,
        workspaceId: segment,
      };
    }
    if (entityType === "process") {
      return {
        href,
        isLast,
        entityType: "process" as const,
        processId: segment,
      };
    }

    return {
      href,
      name: routeNameMap[segment] || segment,
      isLast,
      entityType: null,
    };
  });

  // If we're on a direct workspace route and came from automation server, prepend automation server breadcrumb
  if (isDirectWorkspaceRoute && cameFromAutomationServer) {
    const workspaceId = pathnames[1];
    return (
      <Breadcrumb>
        <BreadcrumbList>
          <WorkspaceBreadcrumbsWithServer
            workspaceId={workspaceId}
            automationServersData={automationServersData}
          />
        </BreadcrumbList>
      </Breadcrumb>
    );
  }

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {breadcrumbs.map((crumb, index) => {
          if ("serverId" in crumb) {
            return (
              <AutomationServerBreadcrumbItem
                key={crumb.href}
                href={crumb.href}
                serverId={crumb.serverId}
                isLast={crumb.isLast}
                showSeparator={index > 0}
                automationServersData={automationServersData}
                isLoading={automationServersLoading}
              />
            );
          }

          if ("workspaceId" in crumb) {
            return (
              <WorkspaceBreadcrumbItem
                key={crumb.href}
                href={crumb.href}
                workspaceId={crumb.workspaceId}
                isLast={crumb.isLast}
                showSeparator={index > 0}
              />
            );
          }

          if ("processId" in crumb) {
            return (
              <ProcessBreadcrumbItem
                key={crumb.href}
                href={crumb.href}
                processId={crumb.processId}
                isLast={crumb.isLast}
                showSeparator={index > 0}
                processes={processes}
                isLoading={automationsLoading}
              />
            );
          }

          return (
            <React.Fragment key={crumb.href}>
              {index > 0 && <BreadcrumbSeparator />}
              <BreadcrumbItem>
                {crumb.isLast ? (
                  <BreadcrumbPage>{crumb.name}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link to={crumb.href}>{crumb.name}</Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
            </React.Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

function renderBreadcrumbContent(
  name: string | null,
  href: string,
  isLast: boolean,
) {
  const content = name ?? <NamePlaceholder />;
  if (isLast) {
    return <BreadcrumbPage>{content}</BreadcrumbPage>;
  }
  return (
    <BreadcrumbLink asChild>
      <Link to={href}>{content}</Link>
    </BreadcrumbLink>
  );
}

function WorkspaceBreadcrumbsWithServer({
  workspaceId,
  automationServersData,
}: {
  workspaceId: string;
  automationServersData: ReturnType<typeof useAutomationServersQuery>["data"];
}) {
  const { data: workspace, isLoading: workspaceLoading } = useWorkspaceByIdQuery(workspaceId);

  const automationServer = workspace?.automation_server
    ? automationServersData?.pages
        .flatMap((page) => page.results)
        .find((s) => s.automation_server_id === workspace.automation_server)
    : null;

  const workspaceName = workspaceLoading ? null : workspace?.name ?? null;
  const serverName = automationServer?.name ?? null;

  return (
    <>
      <BreadcrumbItem>
        <BreadcrumbLink asChild>
          <Link to={"/automation-servers"}>Automation Servers</Link>
        </BreadcrumbLink>
      </BreadcrumbItem>
      {automationServer && (
        <>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to={`/automation-servers/${automationServer.automation_server_id}`}>
                {serverName ?? <NamePlaceholder />}
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
        </>
      )}
      <BreadcrumbSeparator />
      <BreadcrumbItem>
        <BreadcrumbLink asChild>
          <Link to={"/workspaces"}>Workspaces</Link>
        </BreadcrumbLink>
      </BreadcrumbItem>
      <BreadcrumbSeparator />
      <BreadcrumbItem>
        <BreadcrumbPage>
          {workspaceName ?? <NamePlaceholder />}
        </BreadcrumbPage>
      </BreadcrumbItem>
    </>
  );
}

function AutomationServerBreadcrumbItem({
  href,
  serverId,
  isLast,
  showSeparator,
  automationServersData,
  isLoading,
}: {
  href: string;
  serverId: string;
  isLast: boolean;
  showSeparator: boolean;
  automationServersData: ReturnType<typeof useAutomationServersQuery>["data"];
  isLoading: boolean;
}) {
  const server = automationServersData?.pages
    .flatMap((page) => page.results)
    .find((s) => s.automation_server_id === serverId);

  // null = still resolving, render placeholder instead of the raw id.
  const name =
    isLoading || !automationServersData ? null : server?.name ?? null;

  return (
    <>
      {showSeparator && <BreadcrumbSeparator />}
      <BreadcrumbItem>{renderBreadcrumbContent(name, href, isLast)}</BreadcrumbItem>
    </>
  );
}

function WorkspaceBreadcrumbItem({
  href,
  workspaceId,
  isLast,
  showSeparator,
}: {
  href: string;
  workspaceId: string;
  isLast: boolean;
  showSeparator: boolean;
}) {
  const { data: workspace, isLoading } = useWorkspaceByIdQuery(workspaceId);
  const name = isLoading ? null : workspace?.name ?? null;

  return (
    <>
      {showSeparator && <BreadcrumbSeparator />}
      <BreadcrumbItem>{renderBreadcrumbContent(name, href, isLast)}</BreadcrumbItem>
    </>
  );
}

function ProcessBreadcrumbItem({
  href,
  processId,
  isLast,
  showSeparator,
  processes,
  isLoading,
}: {
  href: string;
  processId: string;
  isLast: boolean;
  showSeparator: boolean;
  processes?: Record<string, { id: string; name: string }>;
  isLoading: boolean;
}) {
  const name =
    isLoading || !processes ? null : processes[processId]?.name ?? null;

  return (
    <>
      {showSeparator && <BreadcrumbSeparator />}
      <BreadcrumbItem>{renderBreadcrumbContent(name, href, isLast)}</BreadcrumbItem>
    </>
  );
}
